// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  ICertificationParticipatedIndustriesGetAllAttrs,
  ICertificationParticipatedIndustriesGetAttrs,
  ICertificationParticipatedIndustriesSaveAttrs,
} from 'interfaces/back-office/content-management/certification/participated-industries/certification-participated-industries-view.interface'
import {
  ICertificationParticipatedIndustriesApiGetAllResponse,
  ICertificationParticipatedIndustriesApiGetResponse,
  ICertificationParticipatedIndustriesApiPaginatedResponse,
  ICertificationParticipatedIndustriesApiSaveResponse,
} from 'interfaces/back-office/content-management/certification/participated-industries/certification-participated-industries-api.interface'
import { ICertificationParticipatedIndustriesDetail } from 'interfaces/back-office/content-management/certification/participated-industries'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Certification ParticipatedIndustries Props
const certificationParticipatedIndustriesProops = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  imageURL: types.maybeNull(types.string),
  originalFileName: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Certification Participated Industries List Model
const CertificationParticipatedIndustries = types
  .model('CertificationParticipatedIndustries')
  .props(
    withHelper_baseResponsePaginationTypes(
      types.model('CertificationParticipatedIndustriesList').props(certificationParticipatedIndustriesProops),
    ),
  )

// Certification Participated Industries Detail Model
const CertificationParticipatedIndustriesDetail = types
  .model('CertificationParticipatedIndustriesDetail')
  .props(omit(certificationParticipatedIndustriesProops, ['number']))

const CertificationParticipatedIndustriesStoreModel = types
  .model('CertificationParticipatedIndustries')
  .props({
    certificationParticipatedIndustries_isLoading: types.optional(types.boolean, false),
    certificationParticipatedIndustries_isSaveLoading: types.optional(types.boolean, false),
    certificationParticipatedIndustries_certificationParticipatedIndustries: types.optional(
      CertificationParticipatedIndustries,
      {},
    ),
    certificationParticipatedIndustries_detail: types.optional(CertificationParticipatedIndustriesDetail, {}),
  })
  .actions(self => ({
    certificationParticipatedIndustries_SET_LOADING: (payload: boolean): void => {
      self.certificationParticipatedIndustries_isLoading = payload
    },
    certificationParticipatedIndustries_SET_SAVE_LOADING: (payload: boolean): void => {
      self.certificationParticipatedIndustries_isSaveLoading = payload
    },
    certificationParticipatedIndustries_SET_CERTIFICATION_PARTICIPATED_INDUSTRIES: (
      payload: ICertificationParticipatedIndustriesApiPaginatedResponse,
    ): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.certificationParticipatedIndustries_certificationParticipatedIndustries = cast(mapPayload)
    },
    certificationParticipatedIndustries_SET_CERTIFICATION_PARTICIPATED_INDUSTRY: (
      payload: ICertificationParticipatedIndustriesDetail,
    ): void => {
      self.certificationParticipatedIndustries_detail = cast(payload)
    },
    certificationParticipatedIndustries_CLEAR_CERTIFICATION_PARTICIPATED_INDUSTRIES: (): void => {
      self.certificationParticipatedIndustries_certificationParticipatedIndustries = cast({})
    },
    certificationParticipatedIndustries_CLEAR_CERTIFICATION_PARTICIPATED_INDUSTRY: (): void => {
      self.certificationParticipatedIndustries_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch certification Participated Industries
     *
     * @param {ICertificationParticipatedIndustriesGetAllAttrs} payload
     *
     * @return {any} any
     */
    certificationParticipatedIndustries_fetchCertificationParticipatedIndustries: flow(function* (
      payload: ICertificationParticipatedIndustriesGetAllAttrs,
    ) {
      self.certificationParticipatedIndustries_SET_LOADING(true)

      const response: ICertificationParticipatedIndustriesApiGetAllResponse =
        yield self.environment.certificationParticipatedIndustriesApi.certificationParticipatedIndustries_getCertificationParticipatedIndustries(
          {
            body: { ...payload.body },
            requestConfig: { ...payload.requestConfig },
          },
        )
      if (response.ok) self.certificationParticipatedIndustries_SET_CERTIFICATION_PARTICIPATED_INDUSTRIES(response.res.data)

      self.certificationParticipatedIndustries_SET_LOADING(false)
    }),

    /**
     * @description Fetch certification participated industry
     *
     * @param {ICertificationParticipatedIndustriesGetAttrs} payload
     *
     * @return {any} any
     */
    certificationParticipatedIndustries_fetchCertificationParticipatedIndustry: flow(function* (
      payload: ICertificationParticipatedIndustriesGetAttrs,
    ) {
      self.certificationParticipatedIndustries_SET_LOADING(true)

      const response: ICertificationParticipatedIndustriesApiGetResponse =
        yield self.environment.certificationParticipatedIndustriesApi.certificationParticipatedIndustries_getCertificationParticipatedIndustry(
          {
            params: { id: payload.params.id },
            requestConfig: { ...payload.requestConfig },
          },
        )
      if (response.ok) {
        self.certificationParticipatedIndustries_SET_CERTIFICATION_PARTICIPATED_INDUSTRY(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.certificationParticipatedIndustries_SET_LOADING(false)
    }),

    /**
     * @description Save certification participated industry
     *
     * @param {ICertificationParticipatedIndustriesSaveAttrs} payload
     *
     * @return {any} any
     */
    certificationParticipatedIndustries_saveCertificationParticipatedIndustry: flow(function* (
      payload: ICertificationParticipatedIndustriesSaveAttrs,
    ) {
      self.certificationParticipatedIndustries_SET_SAVE_LOADING(true)

      const response: ICertificationParticipatedIndustriesApiSaveResponse =
        yield self.environment.certificationParticipatedIndustriesApi.certificationParticipatedIndustries_saveCertificationParticipatedIndustry(
          {
            params: { ...payload.params },
            body: { ...payload.body },
            requestConfig: { ...payload.requestConfig },
          },
        )
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.certificationParticipatedIndustries_SET_SAVE_LOADING(false)
    }),
  }))

export const createCertificationParticipatedIndustriesStoreDefaultModel = () =>
  types.optional(CertificationParticipatedIndustriesStoreModel, {})
